import { motion } from "framer-motion";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { useT } from "@/i18n";

const PROVINCES = "Agrigento, Alessandria, Ancona, Aosta, Arezzo, Ascoli Piceno, Asti, Avellino, Bari, Barletta-Andria-Trani, Belluno, Benevento, Bergamo, Biella, Bologna, Bolzano, Brescia, Brindisi, Cagliari, Caltanissetta, Campobasso, Caserta, Catania, Catanzaro, Chieti, Como, Cosenza, Cremona, Crotone, Cuneo, Enna, Fermo, Ferrara, Firenze, Foggia, Forlì-Cesena, Frosinone, Genova, Gorizia, Grosseto, Imperia, Isernia, L'Aquila, La Spezia, Latina, Lecce, Lecco, Livorno, Lodi, Lucca, Macerata, Mantova, Massa-Carrara, Matera, Messina, Milano, Modena, Monza e della Brianza, Napoli, Novara, Nuoro, Oristano, Padova, Palermo, Parma, Pavia, Perugia, Pesaro e Urbino, Pescara, Piacenza, Pisa, Pistoia, Pordenone, Potenza, Prato, Ragusa, Ravenna, Reggio Calabria, Reggio Emilia, Rieti, Rimini, Roma, Rovigo, Salerno, Sassari, Savona, Siena, Siracusa, Sondrio, Taranto, Teramo, Terni, Torino, Trapani, Trento, Treviso, Trieste, Udine, Varese, Venezia, Verbano-Cusio-Ossola, Vercelli, Verona, Vibo Valentia, Vicenza, Viterbo".split(", ");

const HALF = Math.ceil(PROVINCES.length / 2);
const ROWS = [PROVINCES.slice(0, HALF), PROVINCES.slice(HALF)];

const COUNTRIES = [
  { id: "fr", code: "FR", name: "Francia", text: "Fornitura e posa di strutture ad alte prestazioni energetiche." },
  { id: "es", code: "ES", name: "Spagna", text: "Realizzazione di coperture speciali e complessi ibridi." },
  { id: "be", code: "BE", name: "Belgio", text: "Sviluppo di progetti chiavi in mano civili e industriali." },
  { id: "mt", code: "MT", name: "Malta", text: "Soluzioni strutturali in legno lamellare e XLAM adatte al clima e alle architetture del Mediterraneo." },
];

const ProvinceRow = ({ list, reverse }) => (
  <div className="overflow-hidden py-3">
    <div className={`marquee-track flex w-max ${reverse ? "marquee-reverse" : ""}`} style={{ animationDuration: "140s" }}>
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0 items-center gap-6 pr-6" aria-hidden={k === 1}>
          {list.map((p) => (
            <span key={p} className="flex items-center gap-6 whitespace-nowrap font-display text-2xl font-semibold tracking-tight sm:text-4xl">
              {p}<span className="h-2 w-2 shrink-0 rounded-full bg-[#C26D38]" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const Presence = () => {
  const t = useT();
  return (
    <section id="presenza" className="overflow-hidden bg-[#F7F5F0] py-28 text-[#121110] lg:py-40" data-testid="presence-section">
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
        <SectionLabel index="EU">{t("pres.label", "Una presenza capillare")}</SectionLabel>
        <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
          <MaskLines
            className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:col-span-7 lg:text-[4.2rem]"
            lines={[t("pres.h1", "In tutta Italia,"), <span key="p">{t("pres.h2a", "e oltre")} <em className="font-serif font-normal text-[#C26D38]">{t("pres.h2em", "confine.")}</em></span>]}
          />
          <Reveal className="text-base leading-relaxed text-[#6E685E] sm:text-lg lg:col-span-5">
            {t("pres.text", "Progettiamo, trasportiamo e installiamo le nostre strutture in legno lamellare e XLAM in tutte le province d'Italia, garantendo la stessa efficienza da Nord a Sud, isole comprese.")}
          </Reveal>
        </div>
      </div>
      <div className="mt-16 border-y border-[#121110]/10" data-testid="presence-provinces">
        <ProvinceRow list={ROWS[0]} />
        <ProvinceRow list={ROWS[1]} reverse />
      </div>
      <div className="mx-auto mt-24 max-w-[1500px] px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#6E685E]">{t("pres.intl", "Progetti internazionali")}</p>
        <Reveal className="mt-4 max-w-2xl text-base leading-relaxed text-[#6E685E] sm:text-lg">
          {t("pres.intlText", "La qualità certificata dei nostri materiali ci ha permesso di varcare i confini nazionali. Seguiamo grandi progetti strutturali e di bioedilizia in:")}
        </Reveal>
        <div className="mt-10 grid grid-cols-1 border-l border-t border-[#121110]/15 sm:grid-cols-2 lg:grid-cols-4">
          {COUNTRIES.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, delay: i * 0.08 }}
              className="group border-b border-r border-[#121110]/15 p-8 transition-colors duration-500 hover:bg-[#121110] hover:text-[#F7F5F0]"
              data-testid={`presence-country-${c.id}`}
            >
              <p className="font-display text-7xl font-extrabold tracking-tight text-[#C26D38]" style={{ fontStretch: "75%" }}>{c.code}</p>
              <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight">{t(`pres.${c.id}.name`, c.name)}</h3>
              <p className="mt-3 text-base leading-relaxed text-[#6E685E] transition-colors duration-500 group-hover:text-[#A39E93]">{t(`pres.${c.id}.text`, c.text)}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
